import {
  getIncrementDurationMs,
  getIncrementLaneCount,
  type ProgressionSnapshot
} from "./progression";
import type { BoardTile, IncrementLane, IncrementState } from "./types";

type RandomSource = () => number;

function takeRandomTileId(availableTileIds: string[], randomSource: RandomSource): string | null {
  if (availableTileIds.length === 0) {
    return null;
  }

  const index = Math.floor(randomSource() * availableTileIds.length);
  return availableTileIds.splice(index, 1)[0] ?? null;
}

function createIncrementLane(
  tileId: string | null,
  durationMs: number,
  now: number
): IncrementLane {
  return {
    tileId,
    expiresAt: tileId === null ? null : now + durationMs,
    remainingMs: durationMs,
    durationMs,
    burstCount: 0
  };
}

export function createInitialIncrementState(
  progression: ProgressionSnapshot,
  board: readonly BoardTile[],
  now: number,
  randomSource: RandomSource = Math.random
): IncrementState {
  const laneCount = getIncrementLaneCount(progression);
  const durationMs = getIncrementDurationMs(progression);
  const availableTileIds = board.map((tile) => tile.id);

  return {
    lanes: Array.from({ length: laneCount }, () =>
      createIncrementLane(takeRandomTileId(availableTileIds, randomSource), durationMs, now)
    )
  };
}

function seedIncrementLanes(
  increment: IncrementState,
  progression: ProgressionSnapshot,
  now: number
): IncrementLane[] {
  const laneCount = Math.max(increment.lanes.length, getIncrementLaneCount(progression));
  if (increment.lanes.length >= laneCount) {
    return increment.lanes;
  }

  const durationMs = getIncrementDurationMs(progression);

  return [
    ...increment.lanes,
    ...Array.from({ length: laneCount - increment.lanes.length }, () =>
      createIncrementLane(null, durationMs, now)
    )
  ];
}

export function shiftIncrementWindows(increment: IncrementState, deltaMs: number): IncrementState {
  if (deltaMs <= 0) {
    return increment;
  }

  return {
    lanes: increment.lanes.map((lane) =>
      lane.expiresAt === null ? lane : { ...lane, expiresAt: lane.expiresAt + deltaMs }
    )
  };
}

export function syncIncrementState(options: {
  increment: IncrementState;
  board: readonly BoardTile[];
  now: number;
  progression: ProgressionSnapshot;
  randomSource?: RandomSource;
}): IncrementState {
  const randomSource = options.randomSource ?? Math.random;
  const lanes = seedIncrementLanes(options.increment, options.progression, options.now);
  const boardTileIds = new Set(options.board.map((tile) => tile.id));
  const claimedTileIds = new Set<string>();
  const availableTileIds = options.board.map((tile) => tile.id);

  const nextLanes = lanes.map((lane) => {
    if (
      lane.tileId !== null &&
      boardTileIds.has(lane.tileId) &&
      !claimedTileIds.has(lane.tileId)
    ) {
      claimedTileIds.add(lane.tileId);
      const index = availableTileIds.indexOf(lane.tileId);
      if (index >= 0) {
        availableTileIds.splice(index, 1);
      }
      return lane;
    }

    const nextTileId = takeRandomTileId(availableTileIds, randomSource);
    if (nextTileId === null) {
      return {
        ...lane,
        tileId: null,
        expiresAt: null,
        remainingMs: lane.durationMs
      };
    }

    claimedTileIds.add(nextTileId);
    return {
      ...lane,
      tileId: nextTileId,
      expiresAt: options.now + lane.durationMs,
      remainingMs: lane.durationMs
    };
  });

  return { lanes: nextLanes };
}
